<template>
  <er-card v-loading="loading">
    <layout-toolbar
      slot="header"
      type="flex"
      align="flex-start"
      justify="flex-start"
    >
      <p class="card-title">
        {{ $t("Comn_growth") }} vs {{ $t("Comn_biomass") }}
      </p>
    </layout-toolbar>
    <el-row :key="$i18n.locale">
      <high-charts
        :options="chartOptions"
        constructor-type="stockChart"
      ></high-charts>
    </el-row>
  </er-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import chartMixin from "@/mixins/chartMixin";
import { AbwBiomassGraphConfig } from "./chartOptions";
import filtersMixin from "@/mixins/filtersMixin.js";
export default {
  mixins: [errorHandlerMixin, chartMixin, filtersMixin],
  data: function() {
    return {
      abwdata: [],
      weeklygrowth: [],
      farmbiomass: [],
      estimatebiomass: [],
      dateRange: [],
      params: {
        from_date: "",
        to_date: ""
      },
      chartOptions: { ...AbwBiomassGraphConfig },
      loading: false
    };
  },

  computed: {
    ...mapGetters("pondDetails", {
      getSelectedpondAbwVsBiomassGraphData:
        "getSelectedpondAbwVsBiomassGraphData"
    }),
    ...mapGetters("user", {
      getUserCountryName: "getUserCountryName",
      getUserCountryCode: "getUserCountryCode",
      getUserTimeZoneOffset: "getUserTimeZoneOffset"
    }),
    defaultMomentDtRangeForCurrCulture() {
      return this.getMomentObjArrOfCultureDateRange;
    }
  },
  methods: {
    ...mapActions("pondDetails", {
      pondAbwVsBiomassGraphData: "pondAbwVsBiomassGraphData"
    }),
    async handleValueChange(currentSelectedValues) {
      let from_date = this.$moment(currentSelectedValues[0]);
      let to_date = this.$moment(currentSelectedValues[1]);
      if (!this.checkDateWithInCultureRange(from_date)) {
        from_date = this.getMomentObjArrOfCultureDateRange[0];
      }
      if (!this.checkDateWithInCultureRange(to_date)) {
        to_date = this.getMomentObjArrOfCultureDateRange[1];
      }
      this.params.from_date = from_date.format("YYYY-MM-DD");
      this.params.to_date = to_date.format("YYYY-MM-DD");
      await this.initComponent(this.pondId);
    },
    initChart() {
      const graphData = this.$lodash
        .cloneDeep(this.getSelectedpondAbwVsBiomassGraphData)
        .sort((a, b) => a.day_of_culture - b.day_of_culture);
      if (graphData.length === 0) {
        this.chartOptions.series = [];
        return;
      }
      const abwSeriesData = graphData.map(ele => ({
        x: ele.day_of_culture,
        y: ele.abw
      }));
      const growthSeriesData = graphData.map(ele => ({
        x: ele.day_of_culture,
        y: ele.growth
      }));
      const farmBiomassData = graphData.map(ele => ({
        x: ele.day_of_culture,
        y: ele.farm_biomass * (this.getUserCountryName === "india" ? 1 : 1)
      }));
      const estimatedBiomassData = graphData.map(ele => ({
        x: ele.day_of_culture,
        y: ele.biomass_estimate * (this.getUserCountryName === "india" ? 1 : 1)
      }));
      const survivalEstimateData = graphData.map(ele => ({
        x: ele.day_of_culture,
        y: ele.survival_estimate
      }));
      const farmSurvivalData = graphData.map(ele => ({
        x: ele.day_of_culture,
        y: ele.farm_survival
      }));
      this.initChartSeries({
        estimatedBiomassData,
        farmBiomassData,
        farmSurvivalData,
        abwSeriesData,
        growthSeriesData,
        survivalEstimateData
      });
    },
    initChartSeries({
      estimatedBiomassData,
      farmBiomassData,
      farmSurvivalData,
      abwSeriesData,
      growthSeriesData,
      survivalEstimateData
    }) {
      this.chartOptions.series = [];
      [
        {
          name: "ABW",
          type: "column",
          yAxis: 0,
          data: abwSeriesData,
          tooltip: {
            valueSuffix: " g"
          },
          color: "#7394cb"
        },
        {
          name: this.$t("Comn_abw_or_growth"),
          type: "column",
          yAxis: 0,
          data: growthSeriesData,
          tooltip: {
            valueSuffix: " g"
          },
          color: "#90ed7d"
        },
        {
          name: this.$t("Comn_biomass_estimate"),
          type: "line",
          yAxis: 1,
          data: estimatedBiomassData,
          marker: {
            enabled: true
          },
          tooltip: {
            valueSuffix: " Kg"
          },
          color: "#541743"
        },
        {
          name: this.$t("Comn_farm_biomass"),
          type: "line",
          yAxis: 1,
          data: farmBiomassData,
          marker: {
            enabled: true
          },
          tooltip: {
            valueSuffix: " Kg"
          },
          color: "#f79431"
        },
        {
          name: this.$t("Comn_survival_estimate"),
          type: "line",
          yAxis: 1,
          data: survivalEstimateData,
          marker: {
            enabled: true
          },
          tooltip: {
            valueSuffix: " %"
          },
          color: "#48C9B0",
          visible: false
        },
        {
          name: this.$t("Comn_farm_survival"),
          type: "line",
          yAxis: 1,
          data: farmSurvivalData,
          marker: {
            enabled: true
          },
          tooltip: {
            valueSuffix: " %"
          },
          color: "#D98880",
          visible: false
        }
      ].forEach(seriesItem => {
        this.chartOptions.series.push(seriesItem);
      });
    },
    setAxisText() {
      this.chartOptions.xAxis[0].title.text = this.$t(this.xAxisTextKey);
      this.chartOptions.yAxis[0].title.text = this.$t(this.yAxisTextKey[0]);
      this.chartOptions.yAxis[1].title.text = `${this.$t(
        this.yAxisTextKey[1]
      )}(kg)/${this.$t(this.yAxisTextKey[2])}`;
    },
    async initComponent(pondId) {
      this.loading = true;
      try {
        await this.pondAbwVsBiomassGraphData();
        this.pondSlotFeedLogs = this.getPondScheduleGraphSlotWiseFeed;
        this.initAxisTextKeys("Comn_doc_days", [
          "Comn_weight_g",
          "Comn_biomass",
          "Comn_survival_percentage"
        ]);
        this.initChartLang();
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "../../styles/mixins.scss";
.pond-hourly-graph {
  @include responsiveProperty(
    font-size,
    $app_font_size_1,
    $app_font_size_2,
    $app_font_size_3
  );
}
</style>
